import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { format } from "date-fns";
import { ptBR as dateFnsPtBR, enUS, es } from "date-fns/locale";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GlobalAxios } from "../../../../axios/axios-config";
import TableList from "../../../../components/TableList/TableList";
import { IInvAccoPayLoweringsLogData } from "../../../../models/inv-acco-pay-lowerings-log";

const INV_ACCO_PAY_LOWERINGS_LOG_ROUTE =
  process.env.REACT_APP_EXTERNAL +
  "/api/v1/ForubusInvAccoPayLoweringLog/ForubusInvAccoPayLoweringLog";

const INV_ACCO_PAY_LOWERINGS_TOTVS_ROUTE =
  process.env.REACT_APP_EXTERNAL +
  "/api/v1/ForubusInvAccoPayLoweringTotvs/ForubusInvAccoPayLoweringTotvs";

interface ILogDetailData {
  identifier: number;
  logData: string;
  summary?: string | null;
  success: number;
  created: string;
  lastModified: string;
}

interface ITotvsInvoiceData {
  [key: string]: any;
}

interface IInvAccoPayLoweringsDetailsModalProps {
  loweringData: IInvAccoPayLoweringsLogData;
  onClose: () => void;
}

const translationPath = "page.4uBus.logs.invoiceLoweringTotvs.details.";

const InvAccoPayLoweringsDetailsModal: React.FC<
  IInvAccoPayLoweringsDetailsModalProps
> = ({ loweringData, onClose }) => {
  const { t, i18n } = useTranslation();
  const [logData, setLogData] = useState<ILogDetailData[]>([]);
  const [totvsData, setTotvsData] = useState<ITotvsInvoiceData[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState<ILogDetailData | null>(null);
  const [showLogModal, setShowLogModal] = useState(false);
  const [showRawLog, setShowRawLog] = useState(false);
  const [selectedTotvsData, setSelectedTotvsData] = useState<string>("");
  const [showTotvsModal, setShowTotvsModal] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [logsResponse, totvsResponse] = await Promise.all([
          GlobalAxios.get(
            `${INV_ACCO_PAY_LOWERINGS_LOG_ROUTE}?ForubusInvAccoPayLoweringIdentifier=${loweringData.identifier}`,
          ),
          GlobalAxios.get(
            `${INV_ACCO_PAY_LOWERINGS_TOTVS_ROUTE}?ForubusInvAccoPayLoweringIdentifier=${loweringData.invoiceIdentifier}`,
          ),
        ]);

        if (logsResponse.data?.items) {
          setLogData(
            logsResponse.data.items.map(
              (item: ILogDetailData, index: number) => ({
                ...item,
                id: index + 1,
              }),
            ),
          );
        }

        if (totvsResponse.data?.items) {
          setTotvsData(
            totvsResponse.data.items.map(
              (item: ITotvsInvoiceData, index: number) => ({
                ...item,
                id: index + 1,
              }),
            ),
          );
        }
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [loweringData.identifier]);

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return format(date, "Pp", {
      locale:
        i18n.language === "pt-BR"
          ? dateFnsPtBR
          : i18n.language === "en-US"
            ? enUS
            : es,
    }).replaceAll(",", "");
  };

  const handleCloseLogModal = () => {
    setShowLogModal(false);
    setShowRawLog(false);
    setSelectedRow(null);
  };

  const renderSummaryWithCopyActions = (summaryText?: string | null) => {
    const text = summaryText || "Nenhum resumo disponível.";
    const summaryLines = text.split("\n");
    const translatedLabels = {
      provider: t(translationPath + "provider", {
        defaultValue: "Fornecedor",
      }),
      cnpj: t(translationPath + "cnpj", { defaultValue: "CNPJ" }),
      invoice: t(translationPath + "invoice", { defaultValue: "Nota" }),
      id: t(translationPath + "id", { defaultValue: "ID" }),
    };
    const copyTooltipLabel = t("common.copy", { defaultValue: "Copiar" });
    const copyableLabels = new Map<string, keyof typeof translatedLabels>([
      ["Fornecedor", "provider"],
      ["CNPJ", "cnpj"],
      ["Nota", "invoice"],
      ["ID", "id"],
      [translatedLabels.provider, "provider"],
      [translatedLabels.cnpj, "cnpj"],
      [translatedLabels.invoice, "invoice"],
      [translatedLabels.id, "id"],
    ]);
    const messageLines: string[] = [];

    const summaryContent = summaryLines.map((line, index) => {
      const trimmedLine = line.trim();

      if (!trimmedLine) {
        messageLines.push("");
        return null;
      }

      const separatorIndex = trimmedLine.indexOf(":");

      if (separatorIndex === -1) {
        messageLines.push(line);
        return null;
      }

      const label = trimmedLine.slice(0, separatorIndex).trim();
      const value = trimmedLine.slice(separatorIndex + 1).trim();
      const translationKey = copyableLabels.get(label);

      if (!translationKey) {
        messageLines.push(line);
        return null;
      }

      return (
        <Box
          key={`${label}-${index}`}
          sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {translatedLabels[translationKey]}:
          </Typography>
          <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
            {value}
          </Typography>
          <Tooltip title={copyTooltipLabel}>
            <IconButton
              size="small"
              onClick={() => navigator.clipboard.writeText(value.trim())}
            >
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
      );
    });

    const baseMessage = messageLines.join("\n").trim();

    return (
      <>
        {summaryContent}
        {baseMessage && (
          <Typography
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              mt: 2,
            }}
          >
            {baseMessage}
          </Typography>
        )}
      </>
    );
  };

  const logColumns: GridColDef[] = [
    {
      field: "identifier",
      headerName: t(translationPath + "log_id") || "ID do Log",
      width: 80,
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "success",
      headerName: t(translationPath + "status") || "Status",
      width: 100,
      renderCell: (params) => {
        const isSuccess = params.value === 1;
        return (
          <Chip
            label={
              isSuccess
                ? t(translationPath + "success") || "Sucesso"
                : t(translationPath + "error") || "Erro"
            }
            color={isSuccess ? "success" : "error"}
            size="small"
          />
        );
      },
    },
    {
      field: "created",
      headerName: t(translationPath + "created") || "Data de Criação",
      width: 160,
      renderCell: (params) => {
        return formatDate(params.value);
      },
    },
    {
      field: "lastModified",
      headerName: t(translationPath + "last_modified") || "Última Modificação",
      width: 160,
      renderCell: (params) => {
        return formatDate(params.value);
      },
    },
    {
      field: "logData",
      headerName: t(translationPath + "summary") || "Resumo do Log",
      flex: 1,
      minWidth: 300,
      renderCell: (params) => {
        const technicalLogData = params.row.logData || "";
        const displayText = params.row.summary || technicalLogData || "";
        const preview =
          displayText.length > 150
            ? displayText.substring(0, 150) + "..."
            : displayText;

        const handleShowFullLog = () => {
          setSelectedRow(params.row);
          setShowLogModal(true);
        };

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
              }}
            >
              {preview}
            </Typography>
            {technicalLogData && (
              <Tooltip
                title={
                  t(translationPath + "view_full_log") || "Ver log completo"
                }
              >
                <IconButton
                  size="small"
                  onClick={handleShowFullLog}
                  sx={{ padding: 0.5 }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  const totvsColumns: GridColDef[] = [
    {
      field: "invoiceIdentifier",
      headerName:
        t(translationPath + "invoice_identifier") || "Identificador da Nota",
      width: 160,
      renderCell: (params) => params.value ?? "-",
    },
    {
      field: "idLan",
      headerName: t(translationPath + "id_lan") || "ID Lançamento",
      width: 130,
      renderCell: (params) => params.value ?? "-",
    },
    {
      field: "idMov",
      headerName: t(translationPath + "id_mov") || "ID Mov",
      width: 120,
      renderCell: (params) => params.value ?? "-",
    },
    {
      field: "codColigada",
      headerName: t(translationPath + "cod_coligada") || "Cod. Coligada",
      width: 130,
      renderCell: (params) => params.value ?? "-",
    },
    {
      field: "codFilial",
      headerName: t(translationPath + "cod_filial") || "Filial",
      width: 110,
      renderCell: (params) => params.value ?? "-",
    },
    {
      field: "dataVencimento",
      headerName: t(translationPath + "due_date") || "Data de Vencimento",
      width: 180,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "created",
      headerName: t(translationPath + "created") || "Data de Criação",
      width: 180,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "lastModified",
      headerName: t(translationPath + "last_modified") || "Última Modificação",
      width: 180,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "success",
      headerName: t(translationPath + "status") || "Status",
      width: 100,
      renderCell: (params) => {
        const isSuccess = params.value === true;
        return (
          <Chip
            label={
              isSuccess
                ? t(translationPath + "success") || "Sucesso"
                : t(translationPath + "error") || "Erro"
            }
            color={isSuccess ? "success" : "error"}
            size="small"
          />
        );
      },
    },
    {
      field: "totvsData",
      headerName: t(translationPath + "totvs_data") || "Dados TOTVS",
      flex: 1,
      minWidth: 240,
      renderCell: (params) => {
        const value = params.value || "";
        const asString =
          typeof value === "string" ? value : JSON.stringify(value, null, 2);
        const preview =
          asString.length > 140
            ? `${asString.substring(0, 140)}...`
            : asString || "-";

        const handleShow = () => {
          setSelectedTotvsData(asString);
          setShowTotvsModal(true);
        };

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
              }}
            >
              {preview || "-"}
            </Typography>
            {asString && (
              <Tooltip
                title={
                  t(translationPath + "view_totvs_data") || "Ver dados TOTVS"
                }
              >
                <IconButton
                  size="small"
                  onClick={handleShow}
                  sx={{ padding: 0.5 }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <>
      <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
        <DialogActions>
          <Button onClick={onClose}>X</Button>
        </DialogActions>
        <DialogTitle sx={{ paddingY: 0, paddingX: 6, overflow: "hidden" }}>
          {t(translationPath + "title") || "Detalhes da Baixa de Título"} -{" "}
          {loweringData.identifier}
        </DialogTitle>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            paddingX: 3,
          }}
        >
          <Tab label={t(translationPath + "logs") || "Logs"} />
          <Tab label={t(translationPath + "titles_totvs") || "Títulos TOTVS"} />
        </Tabs>
        <DialogContent
          sx={{
            height: 500,
            width: "100%",
            marginTop: 0,
            overflow: "hidden",
          }}
        >
          {tabValue === 0 && (
            <TableList
              columns={logColumns}
              rows={logData}
              fileName={`inv-acco-pay-lowering-logs-${loweringData.identifier}`}
              showSelects={false}
              onRowSelectionModelChange={undefined}
              pageSizeOptions={[5, 10, 25, 50]}
              hideTitle={true}
            />
          )}
          {tabValue === 1 && (
            <TableList
              columns={totvsColumns}
              rows={totvsData}
              fileName={`inv-acco-pay-lowering-totvs-${loweringData.identifier}`}
              showSelects={false}
              onRowSelectionModelChange={undefined}
              pageSizeOptions={[5, 10, 25, 50]}
              hideTitle={true}
            />
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={showLogModal}
        onClose={handleCloseLogModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t(translationPath + "log_data") || "Dados do Log"}
        </DialogTitle>
        <DialogContent>
          {renderSummaryWithCopyActions(
            selectedRow?.summary ||
              selectedRow?.logData ||
              "Nenhum resumo disponível.",
          )}
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-start" }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowRawLog((prev) => !prev)}
            >
              {showRawLog ? "Ocultar log completo" : "Ver log completo"}
            </Button>
          </Box>
          {showRawLog && (
            <Typography
              component="pre"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.8rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                backgroundColor: (theme) => theme.palette.grey[100],
                padding: 2,
                borderRadius: 1,
                maxHeight: 400,
                overflow: "auto",
                mt: 2,
              }}
            >
              {selectedRow?.logData}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLogModal}>
            {t(translationPath + "close") || "Fechar"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showTotvsModal}
        onClose={() => setShowTotvsModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t(translationPath + "totvs_data") || "Dados TOTVS"}
        </DialogTitle>
        <DialogContent>
          <Typography
            component="pre"
            sx={{
              fontFamily: "monospace",
              fontSize: "0.8rem",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              backgroundColor: (theme) => theme.palette.grey[100],
              padding: 2,
              borderRadius: 1,
              maxHeight: 400,
              overflow: "auto",
            }}
          >
            {selectedTotvsData}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTotvsModal(false)}>
            {t(translationPath + "close") || "Fechar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InvAccoPayLoweringsDetailsModal;
